import { t } from '../i18n/i18n';
import { useMemo, useRef, useState } from 'react';
import { stitchTypeName } from '../engine/model/design';
import { moveObject, updateObject } from '../state/designOps';
import { store, useApp } from '../state/store';
import { deleteSelected, duplicateSelected, select, toggleSelect } from './actions';
import { SheetClose } from './PhoneLayout';
import { usePhone } from './usePhone';
import { IconCopy, IconEye, IconGrip, IconEyeOff, IconLock, IconTrash, IconUnlock, IconWarn } from './icons';

const SHORT: Record<string, string> = { fill: 'Fill', satin: 'Satin', running: 'Run' };

export function LayersPanel() {
  const design = useApp((s) => s.history.present);
  const selection = useApp((s) => s.selection);
  const issues = useApp((s) => s.stitch.result?.issues);
  const counts = useApp((s) => s.stitch.result?.objectStitchCounts);
  const [dragId, setDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ id: string; after: boolean } | null>(null);
  const [colorFor, setColorFor] = useState<string | null>(null);
  // phones: HTML drag and drop does not work with fingers, so rows get a grip to drag instead
  const phone = usePhone();
  const palette = useMemo(() => new Map(design.palette.map((c) => [c.id, c])), [design.palette]);
  const severity = useMemo(() => {
    const m = new Map<string, 'error' | 'warning'>();
    for (const i of issues ?? []) {
      if (!i.objectId || i.severity === 'info') continue;
      if (m.get(i.objectId) !== 'error') m.set(i.objectId, i.severity);
    }
    return m;
  }, [issues]);

  const onDrop = (dragged = dragId, target = drop) => {
    if (dragged && target && dragged !== target.id) {
      const from = design.objects.findIndex((o) => o.id === dragged);
      let to = design.objects.findIndex((o) => o.id === target.id) + (target.after ? 1 : 0);
      if (from < to) to--;
      store.commitDesign(moveObject(design, dragged, to));
    }
    setDragId(null);
    setDrop(null);
  };

  /** Finger drag on a row's grip: finds the row under the finger and scrolls the list near its edges. */
  // the latest target is also kept in a ref: the finger can lift before the state has rendered
  const gripDrop = useRef<{ id: string; after: boolean } | null>(null);
  const onGripMove = (e: React.PointerEvent) => {
    if (!dragId) return;
    const row = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('.layer[data-id]');
    if (row?.dataset.id) {
      const r = row.getBoundingClientRect();
      gripDrop.current = { id: row.dataset.id, after: e.clientY > r.top + r.height / 2 };
      setDrop(gripDrop.current);
    }
    const body = (e.currentTarget as Element).closest('.panel-body');
    if (body) {
      const b = body.getBoundingClientRect();
      if (e.clientY < b.top + 32) body.scrollTop -= 10;
      else if (e.clientY > b.bottom - 32) body.scrollTop += 10;
    }
  };

  return (
    <nav className="layers" aria-label={t('Objects')}>
      <div className="panel-head">
        <span style={{ flex: 1 }}>{t('Objects · sewing order')}</span>
        <span style={{ fontWeight: 400, textTransform: 'none' }}>{design.objects.length}</span>
        {phone && <SheetClose />}
      </div>
      <div className="panel-body">
        {!design.objects.length ? (
          <div className="empty">
            <strong>{t('No objects yet')}</strong>
            {t('Import an image for automatic digitizing, or draw shapes with the tools.')}
          </div>
        ) : (
          <ul className="layer-list" role="listbox" aria-multiselectable="true" aria-label={t('Objects in sewing order')}>
            {design.objects.map((o, i) => {
              const c = palette.get(o.colorId);
              const sel = selection.includes(o.id);
              const sev = severity.get(o.id);
              return (
                <li
                  key={o.id}
                  role="option"
                  aria-selected={sel}
                  tabIndex={0}
                  draggable={!phone}
                  data-id={phone ? o.id : undefined}
                  className={
                    'layer' +
                    (sel ? ' selected' : '') +
                    (o.visible ? '' : ' hidden') +
                    (drop?.id === o.id ? (drop.after ? ' drop-after' : ' drop-before') : '')
                  }
                  onClick={(e) => toggleSelect(o.id, e.shiftKey || e.ctrlKey || e.metaKey)}
                  onMouseEnter={() => store.set({ highlight: o.id })}
                  onMouseLeave={() => store.set({ highlight: null })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSelect(o.id, e.shiftKey);
                    }
                  }}
                  onDragStart={(e) => {
                    setDragId(o.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setDrop({ id: o.id, after: e.clientY > r.top + r.height / 2 });
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setDrop(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    onDrop();
                  }}
                  title={t('{name} · {type} · {n} stitches', { name: o.name, type: stitchTypeName(o.params.type), n: counts?.[o.id] ?? '…' })}
                >
                  {phone && (
                    <span
                      className="grip"
                      aria-hidden="true"
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        gripDrop.current = null;
                        setDragId(o.id);
                      }}
                      onPointerMove={onGripMove}
                      onPointerUp={() => onDrop(o.id, gripDrop.current)}
                      onPointerCancel={() => {
                        setDragId(null);
                        setDrop(null);
                      }}
                    >
                      <IconGrip size={18} />
                    </span>
                  )}
                  <span className="idx">{i + 1}</span>
                  <button
                    className="btn small icon ghost"
                    aria-label={t('Colour: {name}. Change colour', { name: c?.name ?? t('missing') })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setColorFor(colorFor === o.id ? null : o.id);
                    }}
                  >
                    <span className="swatch" style={{ background: c?.hex ?? 'transparent' }} />
                  </button>
                  <span className="name">{o.name}</span>
                  {sev && <IconWarn size={13} className={sev === 'error' ? 'err' : 'warn'} aria-label={sev === 'error' ? t('Has errors') : t('Has a warning')} />}
                  {o.visible ? <span className="type">{t(SHORT[o.params.type])}</span> : <span className="type" style={{ color: 'var(--warn)' }}>{t('not sewn')}</span>}
                  <button
                    className={'btn small icon ghost lbtn' + (!o.visible ? ' on' : '')}
                    aria-label={o.visible ? t('Hide (do not sew)') : t('Show (sew)')}
                    title={o.visible ? t('Hide – the object is not sewn and not included in the export') : t('Show – the object is sewn and exported again')}
                    onClick={(e) => {
                      e.stopPropagation();
                      store.commitDesign(updateObject(design, o.id, { visible: !o.visible }));
                    }}
                  >
                    {o.visible ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                  </button>
                  <button
                    className={'btn small icon ghost lbtn' + (o.locked ? ' on' : '')}
                    aria-label={o.locked ? t('Unlock') : t('Lock')}
                    title={o.locked ? t('Unlock') : t('Lock against editing')}
                    onClick={(e) => {
                      e.stopPropagation();
                      store.commitDesign(updateObject(design, o.id, { locked: !o.locked }));
                    }}
                  >
                    {o.locked ? <IconLock size={14} /> : <IconUnlock size={14} />}
                  </button>
                  {colorFor === o.id && (
                    <div
                      role="menu"
                      style={{ position: 'absolute', marginTop: 70, zIndex: 30, background: 'var(--panel-3)', border: '1px solid var(--border)', borderRadius: 6, padding: 6, display: 'flex', flexWrap: 'wrap', gap: 4, width: 200 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {design.palette.map((p) => (
                        <button
                          key={p.id}
                          role="menuitem"
                          className="btn small icon ghost"
                          title={p.name}
                          aria-label={p.name}
                          onClick={() => {
                            store.commitDesign(updateObject(design, o.id, { colorId: p.id }));
                            setColorFor(null);
                          }}
                        >
                          <span className="swatch lg" style={{ background: p.hex, outline: p.id === o.colorId ? '2px solid var(--accent)' : undefined }} />
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {selection.length > 0 && (
        <div className="row" style={{ padding: 8, borderTop: '1px solid var(--border)' }}>
          <button className="btn small" onClick={() => duplicateSelected()}>
            <IconCopy size={14} /> {t('Duplicate')}
          </button>
          <button className="btn small danger" onClick={() => deleteSelected()}>
            <IconTrash size={14} /> {t('Delete')}
          </button>
          <span className="spacer" />
          <button className="btn small ghost" onClick={() => select([])}>
            {t('Clear selection')}
          </button>
        </div>
      )}
    </nav>
  );
}
